import React, { FC, useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

// Components
import BaseContentContainer from '../../components/base-content-container/base-content-container';
import ButtonsBar from '../../components/buttons-bar/buttons-bar';
import Form from '../../components/form/form';
import StudentSelector from '../../components/student-selector/student-selector';
import SubmitButton from '../../components/submit-button/submit-button';
import Loading from '../../components/loading/loading';
import ErrorComponent from '../../components/error/error';

// Form Data
import { formDataNoticeBoardCreate } from '../../form-data/form-data-notice-board-create';

// Helpers
import {
  getNoticeBoardData,
  handleFormOnChange,
  checkIfAllFormHasFilled,
  forceRemovePreviewImage,
  handleStudentChange,
  deleteAttachment,
} from '../../helpers/notice-board-helper';

// Models
import {
  BaseContentContainerActionButtonPosition,
  BaseContentContainerBiggerSide,
  BaseContentContainerFormat,
} from '../../models/base-content-container';

// Providers
import { GeneralContext } from '../../providers/general-provider';

// Services
import ClassroomService from '../../services/classroom-service';
import NoticeBoardService from '../../services/notice-board-service';

const NoticeBoardCreate: FC = () => {
  const navigate = useNavigate();
  const { setToastContent } = useContext(GeneralContext);
  const classroomService = new ClassroomService();
  const [error, setError] = useState(false);
  const initialData: NoticeBoardFormData = getNoticeBoardData();
  const [data, setData] = useState<NoticeBoardFormData>(initialData);
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const [isLoadingAttachment, setIsLoadingAttachment] = useState(false);
  const noticeBoardService = new NoticeBoardService();

  const [noticeBoard] = useState<NoticeBoardCreated>(null);
  const [validations, setValidations] = useState({});
  const [allowSubmit, setAllowSubmit] = useState(true);
  useEffect(() => {
    checkIfAllFormHasFilled(data, validations, allowSubmit, setAllowSubmit);
  }, [validations, allowSubmit, data]); // eslint-disable-line react-hooks/exhaustive-deps

  const [classroomList, setClassroomList] = useState<
    ClassroomStudents[] | null
  >(null);
  useEffect(() => {
    classroomService
      .listClassroomStudents()
      .then((classroomsResponse) => {
        setClassroomList(classroomsResponse);
      })
      .catch((err) => {
        console.error(err);
        return setError(true);
      });
  }, [noticeBoard]); // eslint-disable-line react-hooks/exhaustive-deps

  const createNoticeBoardPromise = (submitData: NoticeBoardFormData) => {
    return noticeBoardService
      .createNoticeBoard(submitData)
      .then(() => {
        setToastContent({
          isOpen: true,
          text: 'Mural salvo!',
          type: 'success',
        });
        navigate('/mural/listar');
      })
      .catch(() => {
        setToastContent({
          isOpen: true,
          text: 'Houve um erro ao salvar o mural.',
          type: 'error',
        });
      });
  };

  const handleSubmit = () => {
    const submitData = Object.assign({}, data);

    createNoticeBoardPromise(submitData);
  };

  if (error) return <ErrorComponent type='contentFull' />;

  if (!classroomList) return <Loading type='contentFull' />;

  return (
    <BaseContentContainer
      biggerSide={BaseContentContainerBiggerSide.right}
      format={BaseContentContainerFormat.doubleWithActionButtons}
      actionButtonPosition={BaseContentContainerActionButtonPosition.bottom}
    >
      <Form
        formData={formDataNoticeBoardCreate()}
        validations={validations}
        handleOnChange={(evt) =>
          handleFormOnChange(
            evt,
            validations,
            data,
            setValidations,
            setData,
            setIsLoadingImage,
            setIsLoadingAttachment
          )
        }
        previewImage={data.formData['image'] ? data.formData['image'] : null}
        forceRemovePreviewImage={(id: string) =>
          forceRemovePreviewImage(id, data, setData)
        }
        isLoadingImage={isLoadingImage}
        attachments={data.formData.attachment ? [data.formData.attachment] : []}
        isLoadingAttachment={isLoadingAttachment}
        deleteAttachment={(id: number) => deleteAttachment(id, data, setData, validations, setValidations)}
      />
      {classroomList && (
        <StudentSelector
          classrooms={classroomList}
          handleStudentChange={(
            selectedClassrooms: number[],
            selectedStudents: number[]
          ) =>
            handleStudentChange(
              selectedClassrooms,
              selectedStudents,
              data,
              setData
            )
          }
          hideStudentSelection={true}
        />
      )}
      <ButtonsBar pattern='firstSeparate' verticalAlign='bottom'>
        <Link to='/mural/listar'>
          <SubmitButton value='cancelar' variant='terciary' />
        </Link>
        <SubmitButton
          value='salvar'
          variant='primary'
          onClick={handleSubmit}
          disabled={!allowSubmit}
        />
      </ButtonsBar>
    </BaseContentContainer>
  );
};

export default NoticeBoardCreate;
